'use client'

import { useMemo } from 'react'

import { useUserContext } from '@/context'

import { useOnboardingExit } from './OnboardingExitContext'
import { hasStartedOnboarding } from './onboarding.persistence'
import { isOnboardingRequired } from './isOnboardingRequired'
import { isOnboardingEnabled } from './onboarding.runtime'
import { useManageActivityProbe } from './useManageActivityProbe'

export const useOnboardingGate = () => {
  const { user, organizationMemberships, isLoadingUser, isClient } =
    useUserContext()
  const onboardingEnabled = isOnboardingEnabled()
  const activityProbe = useManageActivityProbe({ enabled: onboardingEnabled })
  const { isExiting } = useOnboardingExit()

  return useMemo(() => {
    if (!onboardingEnabled) {
      return {
        gateResolved: true,
        isRequired: false,
        isRequirementActive: false,
        pendingResolution: false,
        isProbingActivity: false,
        isExiting,
      }
    }

    const baseRequired = isOnboardingRequired({
      isClient,
      isLoadingUser,
      user,
      organizations: organizationMemberships,
    })

    const onboardingStarted = hasStartedOnboarding(user?.preferences)

    const activityProbeBlocksGate =
      activityProbe.shouldProbe && activityProbe.isPending && !onboardingStarted
    const activityProbeBypassesGate =
      (activityProbe.hasBasicActivity || activityProbe.isError) &&
      !onboardingStarted

    const gateResolved = isClient && !isLoadingUser && !activityProbeBlocksGate

    const isRequirementActive =
      gateResolved && baseRequired && !activityProbeBypassesGate

    const isRequired = isRequirementActive && !isExiting

    return {
      gateResolved,
      isRequired,
      isRequirementActive,
      pendingResolution: !gateResolved,
      isProbingActivity: activityProbe.shouldProbe && activityProbe.isPending,
      isExiting,
    }
  }, [
    activityProbe.hasBasicActivity,
    activityProbe.isError,
    activityProbe.isPending,
    activityProbe.shouldProbe,
    isClient,
    isExiting,
    isLoadingUser,
    onboardingEnabled,
    organizationMemberships,
    user,
  ])
}
